// Shelf System Report PDF Generator
import { jsPDF, defaultAutoTableConfig } from './pdfConfig';
import { 
  safePdfText, 
  createEnhancedTitlePage, 
  addModernSectionHeader,
  addEnhancedPageHeader,
  addEnhancedPageFooter,
  addInfoBox,
  addStatusIndicator 
} from './pdfConfigEnhanced';
import { ShelfSystemInput, ShelfSystemResult, SupportConfiguration } from './ShelfSupportCalculator';
import { convertPolishChars } from '../../utils/pdfFonts';

export class ShelfSystemReportPDF {
  private doc: jsPDF;
  private currentY: number = 20;
  private pageWidth: number = 210;
  private pageHeight: number = 297;
  private margin: number = 15;
  private contentWidth: number = 180;

  constructor() {
    // Constructor only sets initial values
    // Font registration happens in generateReport (async)
    this.currentY = 20;
    this.pageWidth = 210;
    this.pageHeight = 297;
    this.margin = 15;
    this.contentWidth = 180;
  }

  /**
   * Generate complete report
   */
  public generateReport(
    input: ShelfSystemInput,
    result: ShelfSystemResult,
    projectName?: string,
    clientName?: string
  ): void {
    // Create new document
    this.doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    this.debugLog('Starting shelf system report generation');
    // Title page
    this.addTitlePage(projectName || 'System Półkowy', clientName);
    
    // Executive summary
    this.addPage();
    this.addExecutiveSummary(input, result);
    
    // Input parameters
    this.addPage();
    this.addInputParameters(input);
    
    // Optimal configurations
    this.addPage();
    this.addOptimalConfigurations(result.optimalConfigurations);
    
    // Technical drawings
    this.addPage();
    this.addTechnicalDrawings(input, result.optimalConfigurations[0]);
    
    // Installation guide
    if (result.installationGuide && result.installationGuide.length > 0) {
      this.addPage();
      this.addInstallationGuide(result.installationGuide);
    }
    
    // Bill of materials
    this.addPage();
    this.addBillOfMaterials(input, result.optimalConfigurations[0]);
    
    // Recommendations
    if (result.recommendations.length > 0) {
      this.addPage();
      this.addRecommendations(result.recommendations);
    }
  }

  /**
   * Add title page with enhanced design
   */
  private addTitlePage(projectName: string, clientName?: string): void {
    // Use the enhanced title page generator
    createEnhancedTitlePage(
      this.doc,
      'RAPORT TECHNICZNY',
      'System Polkowy z Podporami PMMA',
      projectName,
      clientName
    );
    
    // Add footer
    addEnhancedPageFooter(this.doc, 1);
    return;
    
    // OLD CODE BELOW (commented out for reference)
    // Background gradient effect
    for (let i = 0; i < 50; i++) {
      const gray = 255 - i * 2;
      this.doc.setFillColor(gray, gray, gray);
      this.doc.rect(0, i * 2, this.pageWidth, 2, 'F');
    }
    
    // Logo area with frame
    this.doc.setFillColor(255, 255, 255);
    this.doc.rect(this.pageWidth / 2 - 35, 35, 70, 30, 'F');
    this.doc.setDrawColor(255, 165, 0);
    this.doc.setLineWidth(2);
    this.doc.rect(this.pageWidth / 2 - 35, 35, 70, 30);
    
    // Logo text
    this.doc.setFillColor(255, 165, 0);
    this.doc.rect(this.pageWidth / 2 - 30, 40, 60, 20, 'F');
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(14);
    this.doc.setFont(undefined, 'bold');
    this.doc.text('PLEXISYSTEM', this.pageWidth / 2, 52, { align: 'center' });
    
    // Main title with shadow effect
    this.doc.setTextColor(200, 200, 200);
    this.doc.setFontSize(28);
    this.doc.text('RAPORT TECHNICZNY', this.pageWidth / 2 + 1, 101, { align: 'center' });
    this.doc.setTextColor(50, 50, 50);
    this.doc.text('RAPORT TECHNICZNY', this.pageWidth / 2, 100, { align: 'center' });
    
    // Subtitle
    this.doc.setFontSize(20);
    this.doc.setTextColor(100, 100, 100);
    this.doc.text('System Półkowy z Podporami PMMA', this.pageWidth / 2, 115, { align: 'center' });
    
    // Divider line
    this.doc.setDrawColor(255, 165, 0);
    this.doc.setLineWidth(1);
    this.doc.line(40, 125, this.pageWidth - 40, 125);
    
    // Project info box
    this.doc.setFillColor(248, 248, 248);
    this.doc.rect(40, 135, this.pageWidth - 80, 40, 'F');
    this.doc.setDrawColor(220, 220, 220);
    this.doc.setLineWidth(0.5);
    this.doc.rect(40, 135, this.pageWidth - 80, 40);
    
    // Project name
    this.doc.setFontSize(16);
    this.doc.setTextColor(255, 165, 0);
    this.doc.setFont(undefined, 'bold');
    this.doc.text(projectName, this.pageWidth / 2, 150, { align: 'center' });
    
    // Client
    if (clientName) {
      this.doc.setFontSize(14);
      this.doc.setTextColor(100, 100, 100);
      this.doc.setFont(undefined, 'normal');
      this.doc.text(`Klient: ${clientName}`, this.pageWidth / 2, 165, { align: 'center' });
    }
    
    // Date and time in footer area
    this.doc.setFontSize(11);
    this.doc.setTextColor(120, 120, 120);
    const now = new Date();
    this.doc.text(
      `Wygenerowano: ${now.toLocaleDateString('pl-PL')} ${now.toLocaleTimeString('pl-PL')}`,
      this.pageWidth / 2,
      190,
      { align: 'center' }
    );
    
    // Company info at bottom
    this.doc.setFontSize(10);
    this.doc.setTextColor(150, 150, 150);
    this.doc.text('PlexiSystem Sp. z o.o.', this.pageWidth / 2, 250, { align: 'center' });
    this.doc.text('ul. Przemysłowa 10, 00-001 Warszawa', this.pageWidth / 2, 256, { align: 'center' });
    this.doc.text('tel: +48 123 456 789 | www.plexisystem.pl', this.pageWidth / 2, 262, { align: 'center' });
    
    // Footer
    this.addFooter();
  }

  /**
   * Add executive summary
   */
  private addExecutiveSummary(input: ShelfSystemInput, result: ShelfSystemResult): void {
    this.addSectionTitle('Podsumowanie wykonawcze');
    
    const optimal = result.optimalConfigurations[0];
    if (!optimal) {
      this.doc.setTextColor(255, 0, 0);
      this.doc.text('UWAGA: Nie znaleziono bezpiecznej konfiguracji!', this.margin, this.currentY);
      return;
    }
    
    // Key metrics box
    this.doc.setFillColor(245, 245, 245);
    this.doc.rect(this.margin, this.currentY, this.contentWidth, 60, 'F');
    
    this.currentY += 10;
    this.doc.setFontSize(12);
    this.doc.setTextColor(50, 50, 50);
    
    // Two columns
    const col1X = this.margin + 5;
    const col2X = this.margin + this.contentWidth / 2;
    
    this.doc.text(safePdfText('Rekomendowane rozwiazanie:'), col1X, this.currentY);
    this.currentY += 8;
    
    this.doc.setFontSize(11);
    this.doc.text(safePdfText(`• Typ: ${optimal.type === 'rod' ? 'Pret' : 'Rurka'} PMMA`), col1X, this.currentY);
    this.doc.text(safePdfText(`• Srednica: ${optimal.diameter}mm`), col2X, this.currentY);
    this.currentY += 6;
    
    this.doc.text(safePdfText(`• Liczba podpor: ${optimal.count} szt.`), col1X, this.currentY);
    this.doc.text(safePdfText(`• Wysokosc: ${input.supportHeight}mm`), col2X, this.currentY);
    this.currentY += 6;
    
    this.doc.text(safePdfText(`• Max ugiecie: ${optimal.maxShelfDeflection?.toFixed(2) || '0.00'}mm`), col1X, this.currentY);
    this.doc.text(safePdfText(`• Max naprezenie: ${optimal.maxStressInSupport?.toFixed(1) || '0.0'} MPa`), col2X, this.currentY);
    this.currentY += 6;
    
    this.doc.text(safePdfText(`• Wspolcz. wyboczenia: ${optimal.maxBuckling?.toFixed(1) || '0.0'}`), col1X, this.currentY);
    this.doc.text(safePdfText(`• Margines bezp.: ${optimal.safetyMargin?.toFixed(0) || '0'}%`), col2X, this.currentY);
    this.currentY += 6;
    
    this.doc.text(safePdfText(`• Calkowita waga: ${optimal.totalWeight?.toFixed(1) || '0.0'} kg`), col1X, this.currentY);
    this.doc.text(safePdfText(`• Szacunkowy koszt: ${optimal.totalCost?.toFixed(0) || '0'} PLN`), col2X, this.currentY);
    
    this.currentY += 15;
    
    // Status
    const statusColor = optimal.isSafe ? [0, 150, 0] : [200, 0, 0];
    this.doc.setTextColor(...statusColor);
    this.doc.setFontSize(14);
    this.doc.text(
      safePdfText(optimal.isSafe ? '[OK] KONFIGURACJA BEZPIECZNA' : '[X] KONFIGURACJA NIEBEZPIECZNA'),
      this.pageWidth / 2,
      this.currentY,
      { align: 'center' }
    );
  }

  /**
   * Add input parameters section
   */
  private addInputParameters(input: ShelfSystemInput): void {
    this.addSectionTitle(safePdfText('Parametry wejsciowe'));
    
    const data = [
      ['Parametr', 'Wartosc', 'Jednostka'],
      [safePdfText('Wymiary polki'), '', ''],
      [safePdfText('  Szerokosc'), input.shelfWidth.toString(), 'mm'],
      [safePdfText('  Glebokosc'), input.shelfDepth.toString(), 'mm'],
      [safePdfText('  Grubosc'), input.shelfThickness.toString(), 'mm'],
      [safePdfText('Obciazenie'), '', ''],
      [safePdfText('  Rownomierne'), input.uniformLoad.toString(), 'kg/m2'],
      [safePdfText('  Punktowe'), input.pointLoads ? safePdfText(`${input.pointLoads.length} punktow`) : 'Brak', ''],
      ['System', '', ''],
      [safePdfText('  Wysokosc slupkow'), input.supportHeight.toString(), 'mm'],
      [safePdfText('  Liczba polek'), (input.numberOfShelves || 1).toString(), 'szt.'],
      [safePdfText('  Typ podpor'), input.supportType === 'both' ? safePdfText('Pret i rurka') : input.supportType, ''],
      [safePdfText('Wspolczynniki'), '', ''],
      [safePdfText('  Bezpieczenstwa'), input.safetyFactor.toString(), ''],
      [safePdfText('  Wyboczenia'), (input.bucklingFactor || 2).toString(), '']
    ];
    
    this.doc.autoTable({
      ...defaultAutoTableConfig,
      startY: this.currentY,
      head: [data[0]],
      body: data.slice(1),
      theme: 'striped',
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 60 },
        1: { halign: 'right', cellWidth: 40 },
        2: { cellWidth: 30 }
      },
      didDrawPage: (data: any) => {
        this.addPageFooter(data.pageNumber);
      }
    });
    
    this.currentY = this.doc.lastAutoTable.finalY + 10;
  }

  /**
   * Add optimal configurations comparison
   */
  private addOptimalConfigurations(configs: SupportConfiguration[]): void {
    this.addSectionTitle(safePdfText('Porownanie konfiguracji'));
    
    if (configs.length === 0) {
      this.doc.text(safePdfText('Brak dostepnych konfiguracji'), this.margin, this.currentY);
      return;
    }
    
    const data = configs.slice(0, 5).map((config, index) => [
      `#${index + 1}`,
      config.type === 'rod' ? safePdfText('Pret') : safePdfText('Rurka'),
      `${config.diameter}${config.innerDiameter ? `/${config.innerDiameter}` : ''}`,
      config.count.toString(),
      config.maxShelfDeflection.toFixed(2),
      config.maxStressInSupport.toFixed(1),
      config.maxBuckling.toFixed(1),
      config.totalWeight.toFixed(1),
      config.totalCost.toFixed(0),
      config.isSafe ? '[OK]' : '[X]'
    ]);
    
    this.doc.autoTable({
      startY: this.currentY,
      head: [['#', 'Typ', 'Wymiar [mm]', safePdfText('Ilosc'), safePdfText('Ugiecie [mm]'), safePdfText('Naprezenie [MPa]'), 'Wyboczenie', 'Waga [kg]', 'Koszt [PLN]', 'OK']],
      body: data,
      theme: 'grid',
      headStyles: {
        fillColor: [100, 100, 100],
        textColor: 255,
        fontSize: 9,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 9
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 15 },
        2: { cellWidth: 25 },
        3: { cellWidth: 15, halign: 'center' },
        4: { cellWidth: 20, halign: 'right' },
        5: { cellWidth: 25, halign: 'right' },
        6: { cellWidth: 20, halign: 'right' },
        7: { cellWidth: 20, halign: 'right' },
        8: { cellWidth: 20, halign: 'right' },
        9: { cellWidth: 10, halign: 'center' }
      },
      margin: { left: this.margin, right: this.margin }
    });
    
    this.currentY = this.doc.lastAutoTable.finalY + 10;
  }

  /**
   * Add enhanced technical drawings with better visualization
   */
  private addTechnicalDrawings(input: ShelfSystemInput, config?: SupportConfiguration): void {
    this.addSectionTitle('Rysunki techniczne');
    
    if (!config) {
      this.doc.setTextColor(255, 0, 0);
      this.doc.text('⚠ Brak konfiguracji do wizualizacji', this.margin, this.currentY);
      return;
    }
    
    // Calculate optimal scale
    const maxWidth = 160;
    const maxHeight = 100;
    const scale = Math.min(
      maxWidth / input.shelfWidth,
      maxHeight / input.shelfDepth,
      0.15
    );
    
    const drawingWidth = input.shelfWidth * scale;
    const drawingHeight = input.shelfDepth * scale;
    const startX = this.pageWidth / 2 - drawingWidth / 2;
    const startY = this.currentY + 10;
    
    // Title for top view
    this.doc.setFontSize(11);
    this.doc.setTextColor(50, 50, 50);
    this.doc.setFont(undefined, 'bold');
    this.doc.text('RZUT Z GÓRY - Rozmieszczenie słupków', this.pageWidth / 2, this.currentY, { align: 'center' });
    
    // Background for drawing area
    this.doc.setFillColor(250, 250, 250);
    this.doc.rect(startX - 10, startY - 5, drawingWidth + 20, drawingHeight + 10, 'F');
    
    // Grid lines for reference
    this.doc.setDrawColor(240, 240, 240);
    this.doc.setLineWidth(0.1);
    for (let i = 0; i <= 10; i++) {
      const x = startX + (drawingWidth * i) / 10;
      const y = startY + (drawingHeight * i) / 10;
      this.doc.line(x, startY, x, startY + drawingHeight);
      this.doc.line(startX, y, startX + drawingWidth, y);
    }
    
    // Draw shelf outline with shadow effect
    this.doc.setFillColor(230, 230, 230);
    this.doc.rect(startX + 2, startY + 2, drawingWidth, drawingHeight, 'F');
    this.doc.setFillColor(255, 255, 255);
    this.doc.rect(startX, startY, drawingWidth, drawingHeight, 'F');
    this.doc.setDrawColor(0, 0, 0);
    this.doc.setLineWidth(1);
    this.doc.rect(startX, startY, drawingWidth, drawingHeight);
    
    // Draw support positions with enhanced visualization
    config.positions.forEach((pos, index) => {
      const x = startX + pos.x * scale;
      const y = startY + pos.y * scale;
      const radius = (config.diameter * scale) / 2;
      
      // Shadow for support
      this.doc.setFillColor(200, 200, 200);
      this.doc.circle(x + 1, y + 1, radius, 'F');
      
      if (config.type === 'tube') {
        // Draw tube with gradient effect
        this.doc.setFillColor(255, 165, 0);
        this.doc.circle(x, y, radius, 'F');
        
        if (config.innerDiameter) {
          const innerRadius = (config.innerDiameter * scale) / 2;
          this.doc.setFillColor(255, 255, 255);
          this.doc.circle(x, y, innerRadius, 'F');
          this.doc.setDrawColor(255, 165, 0);
          this.doc.setLineWidth(0.5);
          this.doc.circle(x, y, innerRadius);
        }
        
        this.doc.setDrawColor(200, 100, 0);
        this.doc.setLineWidth(0.5);
        this.doc.circle(x, y, radius);
      } else {
        // Draw rod with 3D effect
        // Gradient circles for 3D effect
        for (let i = radius; i > 0; i -= 0.5) {
          const color = 255 - (radius - i) * 20;
          this.doc.setFillColor(color, Math.floor(color * 0.65), 0);
          this.doc.circle(x, y, i, 'F');
        }
      }
      
      // Add support label with background
      this.doc.setFillColor(255, 255, 255);
      this.doc.rect(x + radius + 2, y - 6, 12, 8, 'F');
      this.doc.setDrawColor(100, 100, 100);
      this.doc.setLineWidth(0.2);
      this.doc.rect(x + radius + 2, y - 6, 12, 8);
      
      this.doc.setFontSize(7);
      this.doc.setTextColor(0, 0, 0);
      this.doc.setFont(undefined, 'bold');
      this.doc.text(`S${index + 1}`, x + radius + 8, y - 1, { align: 'center' });
    });
    
    // Enhanced dimensions with better styling
    this.doc.setFontSize(9);
    this.doc.setTextColor(100, 100, 100);
    this.doc.setFont(undefined, 'normal');
    
    // Width dimension
    this.drawDimensionLine(
      startX,
      startY + drawingHeight + 15,
      startX + drawingWidth,
      startY + drawingHeight + 15,
      `${input.shelfWidth} mm`
    );
    
    // Depth dimension
    this.drawDimensionLine(
      startX - 15,
      startY,
      startX - 15,
      startY + drawingHeight,
      `${input.shelfDepth} mm`,
      true
    );
    
    // Add coordinate labels for corners
    this.doc.setFontSize(7);
    this.doc.setTextColor(150, 150, 150);
    this.doc.text('(0,0)', startX - 3, startY - 2);
    this.doc.text(`(${input.shelfWidth},${input.shelfDepth})`, 
                  startX + drawingWidth - 15, startY + drawingHeight + 5);
    
    this.currentY = startY + drawingHeight + 40;
    
    // Enhanced side view with 3D perspective
    this.doc.setFontSize(11);
    this.doc.setTextColor(50, 50, 50);
    this.doc.setFont(undefined, 'bold');
    this.doc.text('WIDOK BOCZNY - System półkowy', this.pageWidth / 2, this.currentY, { align: 'center' });
    this.currentY += 10;
    
    const sideViewHeight = 80;
    const sideViewWidth = 140;
    const sideStartX = this.pageWidth / 2 - sideViewWidth / 2;
    const sideStartY = this.currentY;
    
    // Background for side view
    this.doc.setFillColor(250, 250, 250);
    this.doc.rect(sideStartX - 10, sideStartY - 5, sideViewWidth + 20, sideViewHeight + 10, 'F');
    
    // Draw supports with 3D effect
    const supportWidth = config.diameter * scale * 2;
    const supportPositions = [
      sideStartX + 20,
      sideStartX + sideViewWidth - 20
    ];
    
    supportPositions.forEach(xPos => {
      // Shadow
      this.doc.setFillColor(200, 200, 200);
      this.doc.rect(xPos + 2, sideStartY + 2, supportWidth, sideViewHeight, 'F');
      
      // Support with gradient
      if (config.type === 'tube') {
        // Outer tube
        this.doc.setFillColor(255, 165, 0);
        this.doc.rect(xPos, sideStartY, supportWidth, sideViewHeight, 'F');
        
        // Inner hollow
        if (config.innerDiameter) {
          const innerWidth = (config.innerDiameter / config.diameter) * supportWidth;
          const innerOffset = (supportWidth - innerWidth) / 2;
          this.doc.setFillColor(255, 255, 255);
          this.doc.rect(xPos + innerOffset, sideStartY, innerWidth, sideViewHeight, 'F');
        }
      } else {
        // Solid rod with gradient
        for (let i = 0; i < supportWidth; i += 2) {
          const color = 255 - (i * 3);
          this.doc.setFillColor(color, Math.floor(color * 0.65), 0);
          this.doc.rect(xPos + i, sideStartY, 2, sideViewHeight, 'F');
        }
      }
      
      // Outline
      this.doc.setDrawColor(100, 100, 100);
      this.doc.setLineWidth(0.5);
      this.doc.rect(xPos, sideStartY, supportWidth, sideViewHeight);
    });
    
    // Draw shelves with perspective
    const shelfCount = input.numberOfShelves || 1;
    const shelfSpacing = input.shelfSpacing || (sideViewHeight / shelfCount);
    
    for (let i = 0; i < shelfCount; i++) {
      const yPos = sideStartY + (i * shelfSpacing);
      const shelfThicknessScale = Math.min(input.shelfThickness * scale, 4);
      
      // Shelf shadow
      this.doc.setFillColor(200, 200, 200);
      this.doc.rect(sideStartX + 2, yPos + 2, sideViewWidth, shelfThicknessScale, 'F');
      
      // Shelf with gradient
      this.doc.setFillColor(240, 240, 240);
      this.doc.rect(sideStartX, yPos, sideViewWidth, shelfThicknessScale, 'F');
      
      // Shelf edge highlight
      this.doc.setFillColor(255, 255, 255);
      this.doc.rect(sideStartX, yPos, sideViewWidth, shelfThicknessScale / 3, 'F');
      
      // Shelf outline
      this.doc.setDrawColor(100, 100, 100);
      this.doc.setLineWidth(0.5);
      this.doc.rect(sideStartX, yPos, sideViewWidth, shelfThicknessScale);
      
      // Shelf label
      this.doc.setFontSize(8);
      this.doc.setTextColor(100, 100, 100);
      this.doc.text(`Półka ${i + 1}`, sideStartX - 20, yPos + shelfThicknessScale / 2 + 2);
    }
    
    // Height dimension
    this.drawDimensionLine(
      sideStartX + sideViewWidth + 15,
      sideStartY,
      sideStartX + sideViewWidth + 15,
      sideStartY + sideViewHeight,
      `${input.supportHeight} mm`,
      true
    );
    
    // Width dimension for shelf
    this.drawDimensionLine(
      sideStartX,
      sideStartY + sideViewHeight + 10,
      sideStartX + sideViewWidth,
      sideStartY + sideViewHeight + 10,
      `${input.shelfWidth} mm`
    );
    
    this.currentY = sideStartY + sideViewHeight + 30;
  }

  /**
   * Add installation guide
   */
  private addInstallationGuide(guide: string[]): void {
    this.addSectionTitle('Instrukcja montażu');
    
    this.doc.setFontSize(11);
    this.doc.setTextColor(50, 50, 50);
    
    guide.forEach((line) => {
      if (this.currentY > this.pageHeight - 30) {
        this.addPage();
        this.currentY = 30;
      }
      
      if (line.startsWith('  ')) {
        // Indented line
        this.doc.setFontSize(10);
        this.doc.text(line, this.margin + 10, this.currentY);
      } else {
        // Main line
        this.doc.setFontSize(11);
        this.doc.setFont(undefined, line.includes(':') ? 'bold' : 'normal');
        this.doc.text(line, this.margin, this.currentY);
      }
      
      this.currentY += 6;
    });
  }

  /**
   * Add bill of materials
   */
  private addBillOfMaterials(input: ShelfSystemInput, config?: SupportConfiguration): void {
    this.addSectionTitle('Zestawienie materiałów');
    
    if (!config) {
      this.doc.text('Brak konfiguracji', this.margin, this.currentY);
      return;
    }
    
    const materials = [
      ['Pozycja', 'Nazwa', 'Specyfikacja', 'Ilość', 'Jednostka', 'Cena jedn.', 'Wartość'],
      [
        '1',
        'Półka PMMA',
        `${input.shelfWidth}x${input.shelfDepth}x${input.shelfThickness}mm`,
        (input.numberOfShelves || 1).toString(),
        'szt.',
        '-',
        '-'
      ],
      [
        '2',
        config.type === 'rod' ? 'Pręt PMMA' : 'Rurka PMMA',
        config.type === 'rod' 
          ? `ø${config.diameter}x${input.supportHeight}mm`
          : `ø${config.diameter}/${config.innerDiameter || '?'}x${input.supportHeight}mm`,
        config.count.toString(),
        'szt.',
        (config.totalCost / config.count).toFixed(2),
        config.totalCost.toFixed(2)
      ],
      [
        '3',
        'Klej do PMMA',
        'Acrifix 192 lub równoważny',
        '1',
        'op.',
        '50.00',
        '50.00'
      ],
      [
        '4',
        'Podkładki poziomujące',
        'Regulowane, PMMA lub metal',
        config.count.toString(),
        'szt.',
        '5.00',
        (config.count * 5).toFixed(2)
      ]
    ];
    
    // Calculate total
    const total = config.totalCost + 50 + (config.count * 5);
    materials.push(['', '', '', '', '', 'SUMA:', `${total.toFixed(2)} PLN`]);
    
    this.doc.autoTable({
      startY: this.currentY,
      head: [materials[0]],
      body: materials.slice(1),
      theme: 'striped',
      headStyles: {
        fillColor: [255, 165, 0],
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 9
      },
      columnStyles: {
        0: { cellWidth: 15, halign: 'center' },
        1: { cellWidth: 35 },
        2: { cellWidth: 45 },
        3: { cellWidth: 15, halign: 'center' },
        4: { cellWidth: 20, halign: 'center' },
        5: { cellWidth: 25, halign: 'right' },
        6: { cellWidth: 25, halign: 'right', fontStyle: 'bold' }
      },
      margin: { left: this.margin, right: this.margin }
    });
    
    this.currentY = this.doc.lastAutoTable.finalY + 10;
  }

  /**
   * Add recommendations section
   */
  private addRecommendations(recommendations: string[]): void {
    this.addSectionTitle('Zalecenia i uwagi');
    
    this.doc.setFontSize(11);
    this.doc.setTextColor(50, 50, 50);
    
    recommendations.forEach((rec) => {
      if (this.currentY > this.pageHeight - 30) {
        this.addPage();
        this.currentY = 30;
      }
      
      // Determine icon based on recommendation type
      let icon = '•';
      let color: [number, number, number] = [50, 50, 50];
      
      if (rec.includes('✅')) {
        icon = '✓';
        color = [0, 150, 0];
      } else if (rec.includes('❌')) {
        icon = '✗';
        color = [200, 0, 0];
      } else if (rec.includes('⚠️')) {
        icon = '!';
        color = [255, 165, 0];
      } else if (rec.includes('💡')) {
        icon = '→';
        color = [0, 100, 200];
      }
      
      // Remove emoji from text
      const cleanText = rec.replace(/[✅❌⚠️💡]/g, '').trim();
      
      this.doc.setTextColor(...color);
      this.doc.text(icon, this.margin, this.currentY);
      
      this.doc.setTextColor(50, 50, 50);
      this.doc.text(cleanText, this.margin + 10, this.currentY);
      
      this.currentY += 8;
    });
  }

  /**
   * Helper: Draw dimension line
   */
  private drawDimensionLine(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    text: string,
    vertical = false
  ): void {
    this.doc.setDrawColor(100, 100, 100);
    this.doc.setLineWidth(0.2);
    
    // Main line
    this.doc.line(x1, y1, x2, y2);
    
    // End markers
    if (vertical) {
      this.doc.line(x1 - 2, y1, x1 + 2, y1);
      this.doc.line(x1 - 2, y2, x1 + 2, y2);
    } else {
      this.doc.line(x1, y1 - 2, x1, y1 + 2);
      this.doc.line(x2, y2 - 2, x2, y2 + 2);
    }
    
    // Text with background
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;
    
    this.doc.setFillColor(255, 255, 255);
    this.doc.setFontSize(8);
    
    if (vertical) {
      // Vertical text placement
      this.doc.rect(x1 - 25, midY - 4, 20, 8, 'F');
      this.doc.setTextColor(50, 50, 50);
      this.doc.text(text, x1 - 15, midY + 1, { align: 'center' });
    } else {
      // Horizontal text placement
      this.doc.rect(midX - 15, y1 - 4, 30, 8, 'F');
      this.doc.setTextColor(50, 50, 50);
      this.doc.text(text, midX, y1, { align: 'center' });
    }
    
    this.doc.setDrawColor(0, 0, 0);
  }

  /**
   * Helper: Add section title
   */
  private addSectionTitle(title: string): void {
    // Use modern section header from enhanced config
    this.currentY = addModernSectionHeader(this.doc, title, this.currentY);
  }

  /**
   * Helper: Add new page
   */
  private addPage(): void {
    this.doc.addPage();
    this.currentY = 20;
    this.addHeader();
    this.addFooter();
  }

  /**
   * Helper: Add header
   */
  private addHeader(): void {
    addEnhancedPageHeader(this.doc, 'RAPORT TECHNICZNY - SYSTEM POLKOWY');
  }

  /**
   * Helper: Add footer
   */
  private addFooter(): void {
    const pageNumber = this.doc.getNumberOfPages();
    addEnhancedPageFooter(this.doc, pageNumber);
  }
  
  /**
   * Add enhanced page footer with page number
   */
  private addPageFooter(pageNumber: number): void {
    const footerY = this.pageHeight - 15;
    
    // Line above footer
    this.doc.setDrawColor(200, 200, 200);
    this.doc.line(12, footerY - 3, this.pageWidth - 12, footerY - 3);
    
    // Footer text
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(8);
    this.doc.setTextColor(120, 120, 120);
    
    // Left - date
    this.doc.text(
      `Wygenerowano: ${new Date().toLocaleDateString('pl-PL')}`,
      12,
      footerY
    );
    
    // Center - page number
    this.doc.text(
      `Strona ${pageNumber}`,
      this.pageWidth / 2,
      footerY,
      { align: 'center' }
    );
    
    // Right - website
    this.doc.text(
      'www.plexisystem.pl',
      this.pageWidth - 12,
      footerY,
      { align: 'right' }
    );
  }

  /**
   * Save the PDF
   */
  public save(filename: string): void {
    this.doc.save(filename);
  }

  /**
   * Get PDF as blob
   */
  public getBlob(): Blob {
    return this.doc.output('blob');
  }

  /**
   * Get PDF as base64
   */
  public getBase64(): string {
    return this.doc.output('datauristring');
  }
  
  /**
   * Debug logging
   */
  private debugLog(message: string): void {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      console.log(`[PDF Shelf] ${message}`);
    }
  }
}

// Export singleton instance
export const shelfSystemReportGenerator = new ShelfSystemReportPDF();
